import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, Subject, filter, of, switchMap, take, tap } from 'rxjs';

import { RestapiService } from '@app/services/restapi.service';
import { TurnoversService } from '@app/services/turnovers.service';
import { ApplicationMenuService } from '@main-application/application-menu/application-menu.service';
import { allPortfoliosValue } from '@main-application/application-menu/property-selector/constants';
import { CommonInMemoryUpdatingService } from '@main-application/shared/services/common-in-memory-updating.service';
import {
  BoardSummaryReportQueryParams,
  SummaryReportOffline,
} from '@main-application/shared/services/summary-report-settings.service';
import { selectUserPermissionLevel } from '@main-application/store/selectors/user.selectors';
import { PermissionLevelType } from '@shared/enums/permission-level.enum';
import { checkPermissionLevel } from '@shared/functions/check-permission-level.function';
import { RestTurnoverTaskModel } from '@shared/interfaces/turnover.interface';
import {
  BoardsSummaryStatsDetails,
  SummaryFilters,
  SummaryStatsGroup,
} from '@ui-components/components/summary-expandable-table/summary-expandable-table.model';
import { DialogResult } from '@ui-components/modals/config/dialog-result.enum';
import { ModalsService } from '@ui-components/modals/modals.service';

import {
  Board,
  BoardChangesNotification,
  BoardLayoutInspectionTemplatesUsage,
  RestBoardModel,
} from '../interfaces/board';

export enum TurnStepScheduleSummaryGroupType {
  None = 0,
  BoardType = 1,
  Board = 2,
  PropertyGroup = 3,
  Property = 4,
  Portfolio = 5,
  UnitType = 6,
  TurnCurrentStepAssignee = 7,
  Activity = 98,
  Assignee = 99,
}

@Injectable()
export class BoardService extends CommonInMemoryUpdatingService<RestBoardModel, void> {
  private readonly route = 'boards/';
  boardRemoving$ = new BehaviorSubject(0);
  boardSyncTrigger$ = new Subject();
  boardNotificationsTrigger$ = new Subject();

  constructor(
    private store: Store<{}>,
    private service: RestapiService,
    private modalsService: ModalsService,
    private applicationMenuService: ApplicationMenuService
  ) {
    super();
  }

  getTurnoverList(boardId: number, portfolioId: number, includeAttachments = false) {
    return this.service.getData<RestTurnoverTaskModel[]>(
      `turnovers/bb/${boardId}/${portfolioId}${includeAttachments ? '?includeAttachments=true' : ''}`,
      TurnoversService.fixTimezoneForTurnoverTaskModel
    );
  }

  refresh() {
    if (this._list$ && !this._list$.closed) {
      return this.getListInternal().pipe(tap(list => this._list$.next(list)));
    }
    return of(null);
  }

  getBoardById(boardId: number): Observable<RestBoardModel> {
    return this.service.getData<RestBoardModel>(this.route + boardId);
  }

  getListInternal(): Observable<RestBoardModel[]> {
    return this.service.getData<RestBoardModel[]>(this.route + 'list');
  }

  protected deleteInternal(id: number): Observable<void> {
    return this.service.post(this.route + `delete/${id}`);
  }

  getListByPortfolio(portfolioId: number) {
    return this.service.getData<RestBoardModel[]>(this.route + 'getByPortfolio/' + portfolioId);
  }

  add(entity: RestBoardModel) {
    return this.service
      .post<number>(this.route + 'add', entity)
      .pipe(tap(id => this.updateSavedData(list => [...list, { ...entity, id: id }])));
  }

  update(entity: RestBoardModel) {
    return this.service.post(this.route + 'update', entity).pipe(
      tap(() =>
        this.updateSavedData(list => {
          const index = list.findIndex(e => e.id === entity.id);
          const copyOfArray = [...list];
          if (index >= 0) {
            copyOfArray.splice(index, 1, entity);
          }
          return copyOfArray;
        })
      )
    );
  }

  removeBoard(boardName: string, boardId: number) {
    return this.store.select(selectUserPermissionLevel).pipe(
      take(1),
      filter(
        (permissionLevel: PermissionLevelType[]) =>
          !!permissionLevel?.length && checkPermissionLevel(permissionLevel, PermissionLevelType.Board_Edit)
      ),
      switchMap(() =>
        this.modalsService
          .openConfirmationModal({
            title: `Delete board: "${boardName}"?`,
            content: `Board will be deleted permanently along with its data.`,
            confirmColor: 'warn',
          })
          .afterClosed()
          .pipe(
            take(1),
            filter(result => result === DialogResult.Success),
            switchMap(() => this.delete(boardId)),
            tap(() => this.boardRemoving$.next(boardId))
          )
      )
    );
  }

  getTemplatesUsage(id: number): Observable<BoardLayoutInspectionTemplatesUsage[]> {
    return this.service.getData<BoardLayoutInspectionTemplatesUsage[]>(`${this.route}${id}/templatesUsage`);
  }

  updateTemplateUsage(usage: BoardLayoutInspectionTemplatesUsage): Observable<void> {
    return this.service.update<void>(`${this.route}updateTemplateUsage`, usage);
  }

  getBoardSummaryStats(filter: SummaryFilters): Observable<SummaryStatsGroup[]> {
    let url = `turnovers/bb/summaryStats/${filter.groupBy ?? 0}/${filter.subGroupBy ?? 0}?`;

    if (filter.portfolioId !== null && filter.portfolioId !== undefined) url += `&portfolioId=${filter.portfolioId}`;
    if (filter.collectionIds) url += `&collectionIds=${filter.collectionIds}`;
    if (filter.propertyIds) url += `&propertyIds=${filter.propertyIds}`;
    if (filter.unitType !== null && filter.unitType !== undefined) url += `&unitType=${filter.unitType}`;
    if (filter.turnCurrentStepAssignee !== null && filter.turnCurrentStepAssignee !== undefined)
      url += `&turnCurrentStepAssignee=${filter.turnCurrentStepAssignee}`;
    if (filter.boardType !== null && filter.boardType !== undefined) url += `&boardType=${filter.boardType}`;
    if (filter.boardId !== null && filter.boardId !== undefined) url += `&boardId=${filter.boardId}`;
    if (filter.startDate) url += `&startDate=${filter.startDate}`;
    if (filter.endDate) url += `&endDate=${filter.endDate}`;

    return this.service.getData<SummaryStatsGroup[]>(url);
  }

  getBoardSummaryDetails(filter: SummaryFilters): Observable<BoardsSummaryStatsDetails> {
    let url = 'turnovers/bb/summaryStatsDetails?';

    if (filter.portfolioId !== null && filter.portfolioId !== undefined) url += `&portfolioId=${filter.portfolioId}`;
    if (filter.collectionIds) url += `&collectionIds=${filter.collectionIds}`;
    if (filter.propertyIds) url += `&propertyIds=${filter.propertyIds}`;
    if (filter.unitType !== null && filter.unitType !== undefined) url += `&unitType=${filter.unitType}`;
    if (filter.turnCurrentStepAssignee !== null && filter.turnCurrentStepAssignee !== undefined)
      url += `&turnCurrentStepAssignee=${filter.turnCurrentStepAssignee}`;
    if (filter.boardType !== null && filter.boardType !== undefined) url += `&boardType=${filter.boardType}`;
    if (filter.boardId !== null && filter.boardId !== undefined) url += `&boardId=${filter.boardId}`;
    if (filter.startDate) url += `&startDate=${filter.startDate}`;
    if (filter.endDate) url += `&endDate=${filter.endDate}`;

    return this.service.getData<BoardsSummaryStatsDetails>(url);
  }

  downloadBoardReport(settings: BoardSummaryReportQueryParams): Observable<SummaryReportOffline> {
    return this.service.post<SummaryReportOffline>('turnovers/bb/exportSummary/offline', settings);
  }

  getBoardChanges(boardId: number, portfolioId: number, startingFrom): Observable<RestTurnoverTaskModel[]> {
    const url = `turnovers/bb/${boardId}/${portfolioId}?modifiedSince=${startingFrom}`;
    return this.service.getData<RestTurnoverTaskModel[]>(url, TurnoversService.fixTimezoneForTurnoverTaskModel);
  }

  getBoardNotifications(
    boardId: number,
    afterCommentId?: number,
    beforeCommentId?: number,
    portfolioId?: number
  ): Observable<BoardChangesNotification[]> {
    const limit = 500;
    const afterComment = afterCommentId ? '&addAfter=' + afterCommentId : '';
    const beforeComment = beforeCommentId ? '&latestId=' + beforeCommentId : '';
    const portfolio = portfolioId ? '&portfolioId=' + portfolioId : '';
    const url = `turnovers/bb/${boardId}/allComments?limit=${limit}${afterComment}${beforeComment}${portfolio}`;
    return this.service.getData<BoardChangesNotification[]>(url);
  }

  filterPortfolioSelectorByBoardPortfolioIds(board: Board | null): void {
    let portfoliosToDisplayIds = null;
    let excludedProperties = null;

    if (
      board?.selectedPortfolioIds?.length > 1 ||
      (board?.selectedPortfolioIds?.length === 1 && board.selectedPortfolioIds[0] !== allPortfoliosValue)
    ) {
      portfoliosToDisplayIds = board.selectedPortfolioIds;
    }

    if (board?.excludedPropertyIds?.length > 0) {
      excludedProperties = board.excludedPropertyIds;
    }

    this.applicationMenuService.portfoliosToDisplay$.next({ portfoliosToDisplayIds, excludedProperties });
  }
}
